// src/components/StudentTable.js

import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Paper, Checkbox, TextField, Chip, Tooltip, IconButton, Avatar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Person as PersonIcon } from '@mui/icons-material';
import {
  getStudentThumbnailUrl,
  getStudentFullImageUrl
} from '../utils/studentPhotoUrl';

const getStatusStyle = (status) => {
  switch (status) {
    case 'Paid':
      return {
        padding: '6px 12px',
        color: '#1a7f37',
        backgroundColor: '#e6f4ea',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 600,
        border: '1px solid #1a7f37',
      };
    case 'Unpaid':
      return {
        padding: '6px 12px',
        color: '#d14343',
        backgroundColor: '#fde7e9',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 600,
        border: '1px solid #d14343',
      };
    default:
      return {
        padding: '6px 12px',
        color: '#856404',
        backgroundColor: '#fff3cd',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 600,
        border: '1px solid #856404',
      };
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Paid':
      return <CheckCircleIcon style={{ fontSize: '16px' }} />;
    case 'Unpaid':
      return <CancelIcon style={{ fontSize: '16px' }} />;
    default:
      return <PendingIcon style={{ fontSize: '16px' }} />;
  }
};

const StudentTable = ({ 
  students, 
  selected, 
  handleSelect,
  handleAmountChange,
  handleSelectAll,
  handlePayment,
  selectedDate,
  getPaymentStatusForDate,
  canteenStatus,
  studentAmounts = {},
  currencySymbol = '₵' 
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [students.length]);

  const paginatedStudents = React.useMemo(
    () => students.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [students, page, rowsPerPage]
  );

  const visibleIds = React.useMemo(
    () => paginatedStudents.map((s) => s._id),
    [paginatedStudents]
  );

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selected.includes(id));

  return (
    <TableContainer component={Paper}>
      <div style={{ 
        padding: '15px 20px', 
        backgroundColor: '#e3f2fd', 
        borderBottom: '1px solid #bbdefb',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ fontWeight: 'bold', color: '#1976d2' }}>📅</span>
        <span style={{ color: '#1976d2', fontWeight: '500' }}>
          Showing canteen fees status for: <strong>{new Date(selectedDate).toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</strong>
        </span>
        {canteenStatus && (
          <span style={{ fontSize: '12px', color: '#666', marginLeft: 'auto' }}>
            Data from backend • Last sync: {new Date().toLocaleTimeString()}
          </span>
        )}
      </div>
      
      <Table sx={{ minWidth: 650 }} aria-label="canteen fees table">
        <TableHead>
          <TableRow style={{ backgroundColor: '#f5f5f5' }}>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={someVisibleSelected && !allVisibleSelected}
                checked={allVisibleSelected}
                onChange={(e) => handleSelectAll(e.target.checked, visibleIds)}
              />
            </TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Photo</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Student</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Class</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Payment Status</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Amount</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Payment Details</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedStudents.map((student) => {
            const paymentStatus = getPaymentStatusForDate(student, selectedDate);
            const isSelected = selected.includes(student._id);
            const isPaid = paymentStatus.status === 'Paid';
            
            return (
              <TableRow
                key={student._id}
                style={{ 
                  backgroundColor: isPaid ? '#f8fff8' : '#fff8f8',
                  opacity: isPaid ? 0.8 : 1
                }}
                hover
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleSelect(student._id)}
                    color={isPaid ? "success" : "primary"}
                  />
                  {isPaid && (
                    <div style={{ fontSize: '10px', color: '#1a7f37', textAlign: 'center' }}>
                      Paid
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Avatar
                    src={getStudentThumbnailUrl(student) || undefined}
                    alt={student.lastname || ''}
                    sx={{ width: 40, height: 40 }}
                    imgProps={{
                      onError: (e) => {
                        const full = getStudentFullImageUrl(student);
                        if (full && e.currentTarget.src !== full) {
                          e.currentTarget.src = full;
                          return;
                        }
                        e.currentTarget.removeAttribute('src');
                      }
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                </TableCell>
                <TableCell>
                  <div>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {student.lastname} {student.othernames}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      ID: {student.ID}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={student.class_} 
                    size="small" 
                    style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                  />
                </TableCell>
                <TableCell>
                  <div style={getStatusStyle(paymentStatus.status)}>
                    {getStatusIcon(paymentStatus.status)}
                    {paymentStatus.status}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {isPaid && (
                      <div style={{ color: '#1a7f37', fontWeight: '600', fontSize: '12px' }}>
                        Paid: {currencySymbol} {parseFloat(paymentStatus.amount).toFixed(2)}
                      </div>
                    )}
                    <TextField
                      key={`amount-field-${student._id}`}
                      size="small"
                      type="number"
                      placeholder="Enter amount"
                      value={studentAmounts[student._id] || ''}
                      onChange={(e) => handleAmountChange(student._id, e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      style={{ width: '120px' }}
                      label={isPaid ? "Update Amount" : "Amount"}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  {isPaid ? (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                        <ReceiptIcon style={{ fontSize: '14px' }} />
                        <span style={{ fontWeight: '500' }}>
                          {paymentStatus.receiptNumber || 'No Receipt'}
                        </span>
                      </div>
                      <div>
                        Paid: {paymentStatus.paidDate ? 
                          new Date(paymentStatus.paidDate).toLocaleDateString() : 
                          'Unknown date'
                        }
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                      No payment recorded
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {isPaid ? (
                    <Tooltip title="Payment completed">
                      <IconButton size="small" disabled>
                        <CheckCircleIcon style={{ color: '#1a7f37' }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Mark as paid">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => {
                          handleSelect(student._id);
                          if (student.amount) {
                            handlePayment();
                          }
                        }}
                        disabled={!student.amount}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 25, 50, 100]}
        count={students.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
      
      {students.length === 0 && (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ fontSize: '18px', marginBottom: '10px' }}>
            No students found for the selected criteria
          </div>
          <div style={{ fontSize: '14px' }}>
            Try adjusting your search, class filter, or date selection
          </div>
        </div>
      )}
    </TableContainer>
  );
};

export default StudentTable;
